import { useState, useEffect } from 'react';
import { useAuthStore } from '../../hooks/useAuth';
import { formatDateInTimeZone } from '../../utils/timezoneDisplay';
import toast from 'react-hot-toast';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    Clock,
    Mail,
    ExternalLink,
    AlertTriangle,
    Calendar,
    Hash
} from 'lucide-react';
import { useUIStore } from '../../hooks/useUIStore';

// Minimal interface for the item parts we need
interface RiskItem {
    id: string;
    carrierName: string;
    startAt: string;
    riskLevel: string;
    riskScore: number;
    reasons: string[];
    poNumber: string;
    status: string;
}

interface ReviewQueueDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    items: RiskItem[];
    onAcknowledge: (id: string, snooze?: boolean) => void;
    token: string;
}

export function ReviewQueueDrawer({ isOpen, onClose, items, onAcknowledge, token }: ReviewQueueDrawerProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const { openAppointmentDetail } = useUIStore();
    const [isSending, setIsSending] = useState(false);

    // Reset index when opening or items change significantly, 
    // but try to keep position if just removing one
    useEffect(() => {
        if (!isOpen) return;
        // Clamp index if items length changed (e.g. item acknowledged)
        if (currentIndex >= items.length && items.length > 0) {
            setCurrentIndex(items.length - 1);
        }
    }, [items.length, isOpen]);

    // Window-level Keyboard Navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if user is typing in an input
            if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName) || (e.target as HTMLElement).isContentEditable) {
                return;
            }
            if (e.key === 'ArrowRight') {
                setCurrentIndex(prev => (prev < items.length - 1 ? prev + 1 : prev));
            }
            if (e.key === 'ArrowLeft') {
                setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, items.length]);

    const currentItem = items[currentIndex];
    const hasNext = currentIndex < items.length - 1;
    const hasPrev = currentIndex > 0;

    const handleNext = () => {
        if (hasNext) setCurrentIndex(prev => prev + 1);
    };

    const handlePrev = () => {
        if (hasPrev) setCurrentIndex(prev => prev - 1);
    };

    const handleOutreach = async () => {
        if (!currentItem || !token) return;
        setIsSending(true);
        const toastId = toast.loading('Sending outreach...');

        try {
            const res = await fetch('/api/ai/risk-outreach/email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ appointmentId: currentItem.id })
            });

            if (res.ok) {
                toast.success('Outreach sent successfully', { id: toastId });
            } else {
                toast.error('Failed to send outreach', { id: toastId });
            }
        } catch (e) {
            console.error(e);
            toast.error('Network error sending outreach', { id: toastId });
        } finally {
            setIsSending(false);
        }
    };

    if (!items.length && isOpen) {
        // Auto close if empty? Or show "All done"
        return (
            <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <SheetContent className="sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle>Review Queue</SheetTitle>
                    </SheetHeader>
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
                        <h3 className="text-lg font-medium">All Caught Up!</h3>
                        <p className="text-muted-foreground mt-2">No more at-risk appointments to review.</p>
                        <Button className="mt-6" onClick={onClose}>Close</Button>
                    </div>
                </SheetContent>
            </Sheet>
        );
    }

    if (!currentItem) return null;

    return (
        <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <SheetContent
                className="sm:max-w-md flex flex-col h-full"
            >
                <SheetHeader className="mb-4">
                    <div className="flex items-center justify-between">
                        <SheetTitle>Review Queue</SheetTitle>
                        <span className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
                            {currentIndex + 1} of {items.length}
                        </span>
                    </div>
                    <SheetDescription>
                        Reviewing high-priority risk/delay alerts.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto pr-2 space-y-6">
                    {/* Item Card */}
                    <div className="rounded-xl border border-border bg-card p-5 shadow-sm space-y-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="font-bold text-lg text-foreground">{currentItem.carrierName}</h3>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                    <Badge variant="neutral" className="font-mono text-xs">
                                        PO: {currentItem.poNumber || 'N/A'}
                                    </Badge>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {(() => {
                                            let whTZ = 'America/Los_Angeles';
                                            try {
                                                
                                                whTZ = useAuthStore.getState().user?.timeZone || whTZ;
                                            } catch {}
                                            
                                            return formatDateInTimeZone(currentItem.startAt, whTZ, true);
                                        })()}
                                    </span>
                                </div>
                            </div>
                            <Badge
                                className={`${currentItem.riskLevel?.toLowerCase() === 'high' ? 'bg-red-500/10 text-red-600 border-red-200' : 'bg-orange-500/10 text-orange-600 border-orange-200'}`}
                            >
                                {currentItem.riskLevel}
                            </Badge>
                        </div>

                        <div className="space-y-2">
                            <div className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-2">
                                <AlertTriangle className="w-3 h-3" /> Risk Factors
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {currentItem.reasons?.map((r, i) => (
                                    <span key={i} className="text-xs bg-muted/50 px-2 py-1 rounded-md text-foreground border border-border/50">
                                        {r}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-2">
                            <Hash className="w-3 h-3" /> Score
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                            <div
                                className={`h-full ${currentItem.riskScore > 0.7 ? 'bg-red-500' : 'bg-orange-500'}`}
                                style={{ width: `${currentItem.riskScore * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Primary Actions */}
                    <div className="grid grid-cols-2 gap-3">
                        <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={() => openAppointmentDetail({
                                id: currentItem.id,
                                po_number: currentItem.poNumber || 'N/A',
                                carrier_name: currentItem.carrierName,
                                status: currentItem.status || 'approved',
                                scheduled_time: currentItem.startAt
                            })}
                        >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open Details
                        </Button>
                        <Button
                            variant="outline"
                            className="w-full justify-start"
                            onClick={handleOutreach}
                            disabled={isSending || !token}
                        >
                            <Mail className="w-4 h-4 mr-2" />
                            {isSending ? 'Sending...' : 'Outreach'}
                        </Button>
                    </div>

                    <div className="border-t border-border my-4" />

                    {/* Disposition Actions */}
                    <div className="space-y-3">
                        <Button
                            variant="default"
                            className="w-full bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900"
                            onClick={() => onAcknowledge(currentItem.id)}
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Acknowledge (Clear)
                        </Button>
                        <Button
                            variant="ghost"
                            className="w-full text-muted-foreground"
                            onClick={() => onAcknowledge(currentItem.id, true)}
                        >
                            <Clock className="w-4 h-4 mr-2" />
                            Snooze 24h
                        </Button>
                    </div>
                </div>

                <SheetFooter className="mt-auto border-t border-border pt-4">
                    <div className="flex w-full items-center justify-between">
                        <Button variant="ghost" size="sm" onClick={handlePrev} disabled={!hasPrev}>
                            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleNext} disabled={!hasNext}>
                            Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
